import React, { forwardRef, useEffect, useState, useMemo } from 'react';
import { Box, Grid, Paper, Table, TableBody, TableContainer, TableHead, TableCell, TableRow, Typography } from '@mui/material';
import { StyledTableCellPrint, StyledTableRow } from '../../reusableComponent/Reusables';
import logo from '../../../images/logo-s.png';
import qoiImg from '../../../images/print_header.png';
import styles from '../../../styles.module.css';
import trigaslogo from '../../../images/tri_gases.jpeg';
import depotMasterService from '../../../services/masters/depotMaster.Service';
import cylinderMasterService from '../../../services/masters/cylinderMaster.Service';
import customerMasterService from '../../../services/masters/customerMaster.Service';
import supplierMasterService from '../../../services/masters/supplierMaster.Service';

const EmptyDeliveryChallanPrint = forwardRef(({ printDetails = {} }, ref) => {
  const { totalCylinders } = printDetails;
  const printData = printDetails || {};
  const ROWS_PER_COLUMN = 10;
  const CYLINDERS_PER_PAGE = ROWS_PER_COLUMN * 2;

  // Parse childRows with useMemo
  const childRows = useMemo(() => {
    try {
      if (typeof printData.table_details === 'string' && printData.table_details.trim() !== '') {
        return JSON.parse(printData.table_details);
      } else if (Array.isArray(printData.table_details)) {
        return printData.table_details;
      }
      return [];
    } catch (err) {
      console.error("Invalid JSON in table_details:", err);
      return [];
    }
  }, [printData.table_details]);

  // Create paginatedRows
  const paginatedRows = useMemo(() => {
    const rows = [];
    for (let i = 0; i < childRows.length; i += CYLINDERS_PER_PAGE) {
      rows.push(childRows.slice(i, i + CYLINDERS_PER_PAGE));
    }
    return rows;
  }, [childRows]);

  const login_details = sessionStorage.getItem('loginUser');
  const userD = JSON.parse(login_details || '{}');
  const userInfo = userD?.result?.userInfo;
  const depotId = Number(printData.depot_id || printData.from_depot_id || userInfo?.depot_id);

  const [depots, setDepots] = useState([]);
  const [cylinders, setCylinders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]); // Fixed typo: setSupplier -> setSuppliers
  const [ownerCounts, setOwnerCounts] = useState({ TGPL: 0, customers: {} });

  useEffect(() => {
    async function fetchData() {
      try {
        const [
          depotData,
          cylinderData,
          customerData,
          supplierData,
        ] = await Promise.all([
          depotMasterService.getDepotList({ page: 0, size: 10, searchKey: "" }),
          cylinderMasterService.getCylinderList({ size: 1000, searchKey: "" }),
          customerMasterService.getCustomerList({ page: 0, size: 1000, searchKey: "" }),
          supplierMasterService.getSupplierList({ page: 0, size: 1000, searchKey: "" }),
        ]);

        setDepots(depotData);
        setCylinders(cylinderData);
        setCustomers(customerData);
        setSuppliers(supplierData)

        const counts = { TGPL: 0, customers: {} };
        childRows.forEach((row) => {
          const cylinder = cylinderData.find((cyl) => cyl.cylinder_no === row.cyl_no);
          if (cylinder) {
            if (cylinder.owner === 'TGPL') {
              counts.TGPL += 1;
            } else if (cylinder.owner === 'Customer' && cylinder.customer_id) {
              const customer = customerData.find((cust) => Number(cust.cust_id) === Number(cylinder.customer_id));
              if (customer) {
                const key = customer.cust_short_code || 'Customer';
                counts.customers[key] = {
                  shortCode: key,
                  count: (counts.customers[key]?.count || 0) + 1,
                };
              } else {
                counts.customers['Customer'] = {
                  shortCode: 'Customer',
                  count: (counts.customers['Customer']?.count || 0) + 1,
                };
              }
            } else {
              counts.customers['Customer'] = {
                shortCode: 'Customer',
                count: (counts.customers['Customer']?.count || 0) + 1,
              };
            }
          } else {
            if (row.owner === 'TGPL') {
              counts.TGPL += 1;
            } else {
              counts.customers['Customer'] = {
                shortCode: 'Customer',
                count: (counts.customers['Customer']?.count || 0) + 1,
              };
            }
          }
        });
        setOwnerCounts(counts);
      } catch (error) {
        console.error("Data fetching failed:", error);
      }
    }

    fetchData();
  }, [childRows]);

  const depot = Array.isArray(depots) ? depots.find((d) => Number(d.depot_id) === Number(printData.from_depot_id)) : null;
  const supplier = Array.isArray(suppliers) ? suppliers.find((d) => Number(d.supplier_id) === Number(printData.to_supplier_id)) : null;
  const depotAddress = depot?.depot_address || printData.from_depot_address || 'Depot address not found';
  const supplierAddress = supplier?.supplier_address || printData.supplier_address || 'Supplier address not found';

  return (
    <Box className="print-source" style={{ margin: '30px' }} ref={ref}>
      {paginatedRows.map((pageRows, pageIndex) => (
        <Box
          key={pageIndex}
          sx={{
            margin: '55px 15px 30px 15px',
            boxShadow: 'none',
            backgroundColor: '#ffffff',
            paddingLeft: '20px !important',
            pageBreakAfter: pageIndex < paginatedRows.length - 1 ? 'always' : 'auto',
            position: 'relative',
            pageBreakInside: 'avoid',
          }}
        >
          {/* Page Number */}
          <Typography
            sx={{
              position: 'absolute',
              top: '-50px',
              right: '30px',
              fontSize: '12px',
              fontWeight: 'normal',
            }}
          >
            Page {pageIndex + 1} of {paginatedRows.length}
          </Typography>
          <Grid container>
            <Grid item xs={12}>
              <Box
                style={{
                  backgroundImage: `url(${qoiImg})`,
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  height: '60px',
                  width: '100% !important',
                  display: 'flex',
                  textAlign: 'center',
                  alignItems: 'center',
                }}
              >
                <Typography
                  sx={{
                    textAlign: 'center',
                    marginLeft: '10px',
                    fontSize: '26px',
                    fontWeight: 'bold',
                    padding: '1px',
                    color: '#ffffff',
                    textTransform: 'uppercase',
                  }}
                >
                  Empty Challan
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={8}>
                  <Box>
                    <Typography sx={{ fontWeight: 'bold', fontSize: '18px' }}>
                      TRI GASES PVT. LTD.
                    </Typography>
                    <Typography sx={{ fontSize: '12px' }}>{depotAddress}</Typography>
                    <Typography sx={{ fontSize: '12px' }}>Email - demotes1234@gmail.com</Typography>
                    <Typography sx={{ fontSize: '12px' }}>Website - www.trigases.com</Typography>
                    <Typography sx={{ fontSize: '12px' }}>GSTIN - 27AADCT2097G1Z7</Typography>
                    <Typography sx={{ fontSize: '12px' }}>
                      Contact No : {printData.depot_contact_no || printData.customer_contact_no || '9850211431'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ justifyContent: 'flex-end', alignItems: 'flex-end', width: '100%', display: 'flex' }}>
                    <img
                      src={trigaslogo}
                      style={{ width: 'auto', height: '150px', justifyContent: 'flex-end', display: 'flex', marginTop: '-70px' }}
                      alt="Tri Gases Logo"
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sx={{ borderTop: '1px solid #616161', backgroundColor: '#f0f0f0', minHeight: '10px', marginTop: '8px' }}></Grid>
          </Grid>
          <Box>
            <Grid container spacing={0.6}>
              <Grid item xs={12} sm={7}>
               <Box className={`${styles.print_label_container}`}>
  <Typography className={styles.info_txt2}>To :</Typography>
  <Typography sx={{ fontWeight: 'bold', fontSize: '16px' }}>
    {printData.to_location === 'supplier'
      ? printData.supplier_name || 'N/A'
      : printData.depot_name || printData.to_depot || 'N/A'}
  </Typography>
</Box>
{(printData.to_location === 'supplier' ? supplierAddress : printData.other_depot_address) && (
  <Typography className={styles.info_txt_ans} sx={{ marginLeft: '2px' }}>
    {printData.to_location === 'supplier'
      ? supplierAddress
      : printData.other_depot_address || 'N/A'}
  </Typography>
)}
              </Grid>

              <Grid item xs={12} sm={5}>
                <Box className={`${styles.print_label_container}`}>
                  <Box className={`${styles.print_label_container}`}>
                    <Typography className={styles.info_txt2}>Challan No :</Typography>
                    <Typography className={styles.info_txt_ans2}>{printData.entry_no || 'EC/CD/25-26/0010'}</Typography>
                  </Box>
                  <Box className={`${styles.print_label_container}`}>
                    <Typography className={styles.info_txt2}>Entry Date :</Typography>
                    <Typography className={styles.info_txt_ans2}>{printData.entry_date || '2025-08-18'}</Typography>
                  </Box>
                </Box>
                <Box className={`${styles.print_label_container}`}>
                  <Typography className={styles.info_txt2}>Vehicle :</Typography>
                  <Typography className={styles.info_txt_ans2}>
                    {printData.unregistered_vehicle && printData.unregistered_vehicle.trim() !== ''
                      ? printData.unregistered_vehicle
                      : printData.vehicle_no || 'MH 14 JL 9392'}
                  </Typography>
                </Box>
                <Box className={`${styles.print_label_container}`}>
                  <Typography className={styles.info_txt2}>Driver :</Typography>
                  <Typography className={styles.info_txt_ans2}>
                    {printData.unregistered_driver && printData.unregistered_driver.trim() !== ''
                      ? printData.unregistered_driver
                      : printData.driver_name || 'Bharat Singh'}
                  </Typography>
                </Box>
                <Box className={`${styles.print_label_container}`}>
                  <Typography className={styles.info_txt2}>Product Code :</Typography>
                  <Typography className={styles.info_txt_ans2}>
                    {childRows[0]?.prod_code || printData.prod_code || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
          <TableContainer component={Paper} className={`${styles.list_tbl_container}`} sx={{ boxShadow: 'none', marginTop: '20px', marginBottom: '20px' }}>
            <Grid container spacing={2}>
              {[0, 1].map((colIndex) => {
                const startIndex = colIndex * ROWS_PER_COLUMN;
                const rowsForColumn = pageRows.slice(startIndex, startIndex + ROWS_PER_COLUMN);
                return (
                  <Grid item xs={6} md={6} key={colIndex} sx={{ height: '90% !important' }}>
                    <Table size="small">
                      <TableHead>
                        <StyledTableRow>
                          <StyledTableCellPrint align="center" className={styles.table_head}>
                            Sr No
                          </StyledTableCellPrint>
                          <StyledTableCellPrint align="center" className={styles.table_head}>
                            Cylinder No
                          </StyledTableCellPrint>
                          
                          <StyledTableCellPrint align="center" className={styles.table_head}>
                            Cyl Type
                          </StyledTableCellPrint>
                          <StyledTableCellPrint align="center" className={styles.table_head}>
                            Owner
                          </StyledTableCellPrint>
                        </StyledTableRow>
                      </TableHead>
                      <TableBody>
                        {rowsForColumn.length > 0 ? (
                          rowsForColumn.map((row, index) => (
                            <StyledTableRow key={index}>
                              <StyledTableCellPrint align="center" className={styles.print_table_body}>
                                {pageIndex * CYLINDERS_PER_PAGE + startIndex + index + 1}
                              </StyledTableCellPrint>
                              <StyledTableCellPrint align="center" className={styles.print_table_body}>
                                {row.cyl_no || 'N/A'}
                              </StyledTableCellPrint>
                              <StyledTableCellPrint align="center" className={styles.print_table_body}>
                                {(() => {
                                  const cylStatus = row.status || row.cyl_type || 'Empty';
                                  const cylRemark = row.remark || row.note || row.level || '';
                                  return cylStatus === 'Full' && cylRemark
                                    ? `${cylStatus} (${cylRemark})`
                                    : cylStatus;
                                })()}
                              </StyledTableCellPrint>
                              <StyledTableCellPrint align="center" className={styles.print_table_body}>
                                {row.owner || 'Customer'}
                              </StyledTableCellPrint>
                            </StyledTableRow>
                          ))
                        ) : (
                          <StyledTableRow>
                            <StyledTableCellPrint colSpan={4} align="center" className={styles.print_table_body}>
                              No data available
                            </StyledTableCellPrint>
                          </StyledTableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Grid>
                );
              })}
            </Grid>
          </TableContainer>
        </Box>
      ))}
      {/* Footer on the last page */}
      {paginatedRows.length > 0 && (
        <Box sx={{ margin: '30px 15px', backgroundColor: '#ffffff', paddingLeft: '20px !important', pageBreakBefore: 'never' }}>
          <Box style={{ borderTop: '1px solid #616161', backgroundColor: '#f0f0f0', minHeight: '10px' }}></Box>
          <Box className={`${styles.footer}`} sx={{ paddingTop: '20px', paddingBottom: '20px', marginBottom: '30px' }}>
            <Table className={`${styles.footerTable}`} style={{ minHeight: '67px' }}>
              <TableBody>
                <TableRow>
                  <TableCell
                    sx={{
                      paddingLeft: '0px !important',
                      marginLeft: '0px !important',
                      paddingRight: '0px !important',
                      marginRight: '0px !important',
                    }}
                  >
                    <Box sx={{ padding: '0px !important', margin: '0px !important' }}>
                      <Box
                        className={`${styles.summaryItem}`}
                        sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, marginBottom: '10px' }}
                      >
                        <Typography className={`${styles.label_Txt_add}`}>TGPL:</Typography>
                        <Typography className={`${styles.summary_ans}`}>{ownerCounts.TGPL || 0}</Typography>
                      </Box>
                      <Box
                        className={`${styles.summaryItem}`}
                        sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, marginBottom: '10px' }}
                      >
                        <Typography className={`${styles.label_Txt_add}`}>Customer:</Typography>
                        <Typography className={`${styles.summary_ans}`}>
                          {Object.values(ownerCounts.customers)
                            .map(({ count }) => count)
                            .reduce((a, b) => a + b, 0) || 0}
                        </Typography>
                      </Box>
                      <Box
                        className={`${styles.summaryItem}`}
                        sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, borderBottom: 'none' }}
                      >
                        <Typography className={`${styles.label_Txt_add}`}>Total Cylinders:</Typography>
                        <Typography className={`${styles.summary_ans}`}>
                          {totalCylinders || childRows.length || 0}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Grid container spacing={2} sx={{ mt: 5 }}>
                        <Grid item xs={12}>
                          <Typography className={`${styles.authorized_txt}`}>Receiver Signature</Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography className={`${styles.footer_txt}`}>
                        For <span className={`${styles.footer_txt_bold}`}>TRI GASES PVT. LTD.</span>
                      </Typography>
                      <Grid container spacing={2} sx={{ mt: 3 }}>
                        <Grid item xs={12}>
                          <Typography className={`${styles.authorized_txt}`}>Driver Signature</Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </Box>
      )}
    </Box>
  );
});

export default EmptyDeliveryChallanPrint;